import { createIdempotencyKey, del, get, put, post, ApiError } from './client';
import { MOCK_INVESTMENTS, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/investments';
import type { Deposit, FundHolding, IpoApplication, NpsAccount, SgbHolding } from '@/types/phase4.types';

const unavailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'Investments backend is not available in mock mode.');
};

export interface InvestmentRiskProfile {
  [key: string]: unknown;
}

export interface InvestmentOrderInput {
  productCode: string;
  accountNumber: string;
  amountPaise: number;
  type: string;
}

export interface InvestmentOrder extends Record<string, unknown> {
  id: string;
}

export interface InvestmentProduct {
  id: string;
  code: string;
  name: string;
  type: string;
  minimumAmountPaise: number;
  active: boolean;
}

export type InvestmentHolding = Record<string, unknown>;

const investmentOrderPath = (id: string): string =>
  `/investments/orders/${encodeURIComponent(id)}`;

export const listInvestmentOrders = (): Promise<InvestmentOrder[]> =>
  REAL_API_ENABLED ? get<InvestmentOrder[]>('/investments/orders') : unavailable();

export const createInvestmentOrder = (
  input: InvestmentOrderInput,
  idempotencyKey = createIdempotencyKey(),
): Promise<InvestmentOrder> =>
  REAL_API_ENABLED
    ? post<InvestmentOrder>('/investments/orders', input, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    : unavailable();

export const getInvestmentOrder = (id: string): Promise<InvestmentOrder> =>
  REAL_API_ENABLED
    ? get<InvestmentOrder>(investmentOrderPath(id))
    : unavailable();

export const getInvestmentOrderReceipt = (id: string): Promise<InvestmentOrder> =>
  REAL_API_ENABLED
    ? get<InvestmentOrder>(`${investmentOrderPath(id)}/receipt`)
    : unavailable();

export const listInvestmentProducts = (): Promise<InvestmentProduct[]> =>
  REAL_API_ENABLED
    ? get<InvestmentProduct[]>('/investments/products')
    : unavailable();

export const listInvestmentHoldings = (): Promise<InvestmentHolding[]> =>
  REAL_API_ENABLED
    ? get<InvestmentHolding[]>('/investments/holdings')
    : unavailable();

export const getRiskProfile = (): Promise<InvestmentRiskProfile> => {
  if (REAL_API_ENABLED) return get<InvestmentRiskProfile>('/investments/risk-profile');
  const state = mock.mutualFundState() as Record<string, unknown>;
  return Promise.resolve({
    riskProfile: state.riskScore ?? null,
    kraStatus: state.kra ?? 'VALIDATED',
    ...state,
  });
};

export const saveRiskProfile = (riskProfile: string): Promise<InvestmentRiskProfile> =>
  REAL_API_ENABLED
    ? put<InvestmentRiskProfile>(`/investments/risk-profile?riskProfile=${encodeURIComponent(riskProfile)}`)
    : unavailable();

export interface UpiCollectInput {
  [key: string]: unknown;
}

export const createUpiCollectRequest = (input: UpiCollectInput): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>('/upi/collect', input)
    : unavailable();

export interface InvestmentSwitchInput {
  [key: string]: unknown;
}

export interface InvestmentPlan {
  [key: string]: unknown;
}

export const switchInvestment = (input: InvestmentSwitchInput): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>('/investments/switch', input)
    : unavailable();

export type InvestmentPlanKind = 'SIP' | 'STP' | 'SWP';

export const listInvestmentPlans = (kind?: InvestmentPlanKind): Promise<InvestmentPlan[]> =>
  REAL_API_ENABLED
    ? get<InvestmentPlan[]>('/investments/plans', kind ? { params: { kind } } : undefined)
    : unavailable();

export const createInvestmentPlan = (
  kind: InvestmentPlanKind,
  input: Record<string, unknown>,
): Promise<InvestmentPlan> =>
  REAL_API_ENABLED
    ? post<InvestmentPlan>(`/investments/plans?kind=${encodeURIComponent(kind)}`, input)
    : unavailable();

export const stopInvestmentPlan = (planId: string): Promise<InvestmentPlan> =>
  REAL_API_ENABLED
    ? del<InvestmentPlan>(`/investments/plans/${encodeURIComponent(planId)}`)
    : unavailable();

export const getSgbOffers = (): Promise<Record<string, unknown>[]> =>
  REAL_API_ENABLED ? get<Record<string, unknown>[]>('/investments/sgb') : unavailable();

export const getDematAccount = (): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED ? get<Record<string, unknown>>('/investments/demat') : unavailable();

export const deposits = (): Promise<Deposit[]> => MOCK_INVESTMENTS ? mock.deposits() : unavailable();
export const openDeposit = (input: Parameters<typeof mock.openDeposit>[0]): Promise<Deposit> => MOCK_INVESTMENTS ? mock.openDeposit(input) : unavailable();
export const topUpDeposit = (id: string, amountRupees: number): Promise<Deposit> => MOCK_INVESTMENTS ? mock.topUpDeposit(id, amountRupees) : unavailable();
export const closeDeposit = (id: string): Promise<{ amountRupees: number }> => MOCK_INVESTMENTS ? mock.closeDeposit(id) : unavailable();

export const mutualFundState = () => MOCK_INVESTMENTS ? mock.mutualFundState() : unavailable();
export const onboardMutualFunds = (riskScore: number): Promise<void> => MOCK_INVESTMENTS ? mock.onboardMutualFunds(riskScore) : unavailable();
export const buyFund = (input: Parameters<typeof mock.buyFund>[0]): Promise<FundHolding> => MOCK_INVESTMENTS ? mock.buyFund(input) : unavailable();
export const createSip = (input: Parameters<typeof mock.createSip>[0]): Promise<FundHolding> => MOCK_INVESTMENTS ? mock.createSip(input) : unavailable();
export const redeemFund = (fundId: string, amountRupees: number): Promise<void> => MOCK_INVESTMENTS ? mock.redeemFund(fundId, amountRupees) : unavailable();
export const switchFund = (fromFundId: string, toFundId: string, amountRupees: number): Promise<void> => MOCK_INVESTMENTS ? mock.switchFund(fromFundId, toFundId, amountRupees) : unavailable();

export const ipoState = () => MOCK_INVESTMENTS ? mock.ipoState() : unavailable();
export const linkDemat = (dpId: string, clientId: string): Promise<void> => MOCK_INVESTMENTS ? mock.linkDemat(dpId, clientId) : unavailable();
export const applyIpo = (input: Parameters<typeof mock.applyIpo>[0]): Promise<IpoApplication> => MOCK_INVESTMENTS ? mock.applyIpo(input) : unavailable();
export const cancelIpo = (id: string): Promise<void> => MOCK_INVESTMENTS ? mock.cancelIpo(id) : unavailable();

export const sgbState = () => MOCK_INVESTMENTS ? mock.sgbState() : unavailable();
export const buySgb = (grams: number): Promise<SgbHolding> => MOCK_INVESTMENTS ? mock.buySgb(grams) : unavailable();
export const npsState = (): Promise<NpsAccount | undefined> => MOCK_INVESTMENTS ? mock.npsState() : unavailable();
export const openNps = (input: Parameters<typeof mock.openNps>[0]): Promise<NpsAccount> => MOCK_INVESTMENTS ? mock.openNps(input) : unavailable();
export const contributeNps = (tier: 1 | 2, amountRupees: number): Promise<NpsAccount> => MOCK_INVESTMENTS ? mock.contributeNps(tier, amountRupees) : unavailable();
